import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FolderPlus, ListChecks, Send, ChevronRight, ChevronLeft, X, Sparkles } from "lucide-react";
import API from "@/lib/api";

const STEPS = [
  {
    icon: FolderPlus,
    title: "Create a Project",
    desc: "Set up a new project with client details, sector, type, and size metrics. The 4-step wizard guides you through everything — project info, location, size, and review.",
    action: "/projects/new",
    actionLabel: "Create Project",
    color: "from-emerald-500 to-teal-600",
    bgGlow: "bg-emerald-500/10",
  },
  {
    icon: ListChecks,
    title: "Build the Scope",
    desc: "Load a deliverable template (RIBA or BSRIA), toggle items on or off, and set estimated hours per role. Exclusions and assumptions are generated automatically.",
    action: null,
    actionLabel: "Open a Project",
    color: "from-blue-500 to-indigo-600",
    bgGlow: "bg-blue-500/10",
  },
  {
    icon: Send,
    title: "Submit for Review",
    desc: "Once pricing looks right, submit the project for Director approval. They will review, approve or request revisions — and you will be notified of every status change.",
    action: null,
    actionLabel: "View Approvals",
    color: "from-amber-500 to-orange-600",
    bgGlow: "bg-amber-500/10",
  },
];

export default function QuickStartGuide({ onComplete }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [shouldShow, setShouldShow] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    API.get("/auth/quick-start-status")
      .then((r) => {
        if (!cancelled && r.data.show) {
          setShouldShow(true);
          setOpen(true);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const markComplete = async () => {
    try {
      await API.post("/auth/quick-start-complete");
    } catch {}
    setShouldShow(false);
    setOpen(false);
    onComplete?.();
  };

  const handleAction = () => {
    const s = STEPS[step];
    markComplete();
    if (s.action) {
      navigate(s.action);
    } else if (step === 1) {
      navigate("/projects");
    } else if (step === 2) {
      navigate("/approvals");
    }
  };

  const handleSkip = () => {
    markComplete();
  };

  if (!shouldShow) return null;

  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleSkip(); }}>
      <DialogContent
        className="sm:max-w-lg p-0 overflow-hidden border-border/50 bg-card gap-0"
        data-testid="quick-start-dialog"
      >
        {/* Header strip */}
        <div className="relative px-6 pt-6 pb-4">
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            data-testid="quick-start-skip"
            aria-label="Skip guide"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-xs font-semibold uppercase tracking-widest text-primary">Quick Start Guide</span>
          </div>
          <p className="text-xs text-muted-foreground">3 steps to get you pricing projects</p>

          {/* Step indicators */}
          <div className="flex gap-1.5 mt-4">
            {STEPS.map((_, i) => (
              <div
                key={`step-indicator-${i}`}
                className={`h-1 rounded-full flex-1 transition-all duration-300 ${
                  i <= step ? "bg-primary" : "bg-muted"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Step content */}
        <div className="px-6 pb-6">
          <div className={`rounded-xl p-6 ${current.bgGlow} border border-border/50 transition-all duration-300`}>
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${current.color} flex items-center justify-center mb-4 shadow-lg`}>
              <Icon className="w-6 h-6 text-white" />
            </div>

            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Step {step + 1}</span>
            </div>

            <h3 className="text-xl font-bold text-foreground mb-2 font-heading">{current.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{current.desc}</p>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-5">
            <div className="flex gap-2">
              {step > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setStep(step - 1)}
                  data-testid="quick-start-prev"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSkip}
                className="text-muted-foreground"
                data-testid="quick-start-dismiss"
              >
                Skip guide
              </Button>

              {!isLast ? (
                <Button
                  size="sm"
                  onClick={() => setStep(step + 1)}
                  data-testid="quick-start-next"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={handleAction}
                  className={`bg-gradient-to-r ${current.color} text-white border-0 hover:opacity-90`}
                  data-testid="quick-start-finish"
                >
                  Get Started
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
